import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
} from 'firebase/auth';
import { resetSessionExpiredFlag } from '../config/api';
import { auth, googleProvider } from '../config/firebase';
import { authService } from '../services';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ─── Profile completeness check (fallback when is_onboarding_completed missing) ─
function isProfileComplete(profile, role) {
  if (!profile?.sex || !profile?.phone || !profile?.address || !profile?.dob) return false;
  if (role === 'child') {
    if (!profile?.school_name || !profile?.grade || !profile?.tshirt_size) return false;
  }
  return true;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser]                       = useState(null);
  const [userName, setUserName]               = useState('');
  const [userEmail, setUserEmail]             = useState('');
  const [familyRole, setFamilyRole]           = useState('parent');
  const [avatarUrl, setAvatarUrl]             = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading]             = useState(true);
  const [needOnboarding, setNeedOnboarding]   = useState(false);
  const [needPwdChanged, setNeedPwdChanged]   = useState(false);

  // Overlay shown while kicking + re-signing the user in to refresh JWT claims.
  // Stays visible through the intermediate signOut so the app never flashes the
  // logged-out state. Cleared only when the new user session is confirmed.
  const [isReauthing, setIsReauthing]         = useState(false);
  // Ref so the onAuthStateChanged callback can check without a stale closure
  const reauthingRef = useRef(false);

  // Modal shown when any API call returns 401 (post-retry) or 403.
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // profile.family_role — advisory, used only for onboarding routing (not auth)
  const [onboardingRole, setOnboardingRole]   = useState('parent');
  // tshirt_size cached here to drive the My Camps gate for children
  const [tshirtSize,     setTshirtSize]       = useState('');

  // Stable ref so callbacks can read current familyRole without stale closure
  const familyRoleRef = useRef('parent');
  useEffect(() => { familyRoleRef.current = familyRole; }, [familyRole]);

  // Listen for the global sessionExpired event dispatched by the Axios interceptor
  // whenever a 401 (post-retry) or 403 is received.
  useEffect(() => {
    const handle = () => setIsSessionExpired(true);
    window.addEventListener('sessionExpired', handle);
    return () => window.removeEventListener('sessionExpired', handle);
  }, []);

  // ─── Fetch / init profile ──────────────────────────────────────────────────
  const fetchUserProfile = useCallback(async (roleOverride) => {
    const jwtRole = roleOverride ?? familyRoleRef.current;
    try {
      await authService.initProfile();
      const p = await authService.getProfile();
      if (p?.name)       setUserName(p.name);
      if (p?.avatar_url) setAvatarUrl(p.avatar_url || '');
      // tshirt_size drives the My Camps gate for children
      setTshirtSize(p?.tshirt_size || '');

      // profile.family_role is advisory — drives onboarding flow only, not auth
      const profileRole = p?.family_role ? String(p.family_role).toLowerCase() : null;
      const obRole = profileRole || jwtRole;
      setOnboardingRole(obRole);

      // Use is_onboarding_completed when the backend provides it; fall back to
      // local field-presence check for backward-compatibility.
      const completed = (typeof p?.is_onboarding_completed === 'boolean')
        ? p.is_onboarding_completed
        : isProfileComplete(p, obRole);
      setNeedOnboarding(!completed);
      setNeedPwdChanged(false);
      return p;
    } catch (err) {
      console.warn('Profile fetch failed:', err?.response?.status);
      return null;
    }
  }, []);

  useEffect(() => {
    let resolved = false;
    const authTimeout = setTimeout(() => { if (!resolved) setIsLoading(false); }, 10000);

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      resolved = true;
      clearTimeout(authTimeout);

      if (firebaseUser) {
        setUser(firebaseUser);
        setUserEmail(firebaseUser.email || '');
        setUserName(firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User');
        setIsAuthenticated(true);

        let jwtFamilyRole = 'parent';
        try {
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          const raw = tokenResult.claims.family_role ?? tokenResult.claims.familyRole ?? 'parent';
          jwtFamilyRole = String(raw).toLowerCase();
        } catch (_) {}

        setFamilyRole(jwtFamilyRole);
        familyRoleRef.current = jwtFamilyRole;

        await fetchUserProfile(jwtFamilyRole);

        // If this sign-in completed an explicit reauth cycle, clear both the
        // overlay and the session-expired modal, then reload family data.
        // We only do this when reauthingRef is set — NOT on background token
        // refreshes (which also trigger onAuthStateChanged and would otherwise
        // dismiss the modal prematurely).
        if (reauthingRef.current) {
          reauthingRef.current = false;
          setIsReauthing(false);
          setIsSessionExpired(false);
          resetSessionExpiredFlag();
          window.dispatchEvent(new CustomEvent('familyStatusChanged'));
        }
      } else {
        // signOut fired — if a reauth is in progress, keep the overlay up so
        // the app never flashes the logged-out state between sign-out and the
        // incoming signInWithPopup completing.
        if (!reauthingRef.current) {
          setUser(null);
          setIsAuthenticated(false);
          setNeedOnboarding(false);
          setUserName('');
          setUserEmail('');
          setFamilyRole('parent');
        }
      }
      setIsLoading(false);
    });

    return () => { unsub(); clearTimeout(authTimeout); };
  }, [fetchUserProfile]);

  // Token expiry listener
  useEffect(() => {
    const handle = () => { setIsAuthenticated(false); setUser(null); };
    window.addEventListener('tokenExpired', handle);
    return () => window.removeEventListener('tokenExpired', handle);
  }, []);

  // ─── Auth actions ──────────────────────────────────────────────────────────
  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: cred.user } }));
    return cred;
  };

  const loginWithGoogle = async () => {
    const cred = await signInWithPopup(auth, googleProvider);
    window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: cred.user } }));
    return cred;
  };

  // ─── Seamless re-auth (kick + sign back in) ────────────────────────────────
  // Use this after operations that require fresh JWT claims (e.g. accepting a
  // family invitation that sets a new family_role via beforeSignIn).
  //
  // Flow:
  //  1. Overlay appears immediately — app never flashes the sign-out state.
  //  2. signOut clears the stale local session.
  //  3. signInWithPopup with login_hint — Google skips the account picker and
  //     re-auths the same email in milliseconds (popup barely visible).
  //  4. onAuthStateChanged fires with the fresh user + new JWT claims.
  //  5. Overlay disappears; app renders normally with updated family role.
  const reauthWithRedirect = async () => {
    const email = auth.currentUser?.email;
    reauthingRef.current = true;
    setIsReauthing(true);
    try {
      const provider = new GoogleAuthProvider();
      if (email) {
        // login_hint tells Google exactly which account — skips the chooser screen
        provider.setCustomParameters({ login_hint: email });
      }
      await signOut(auth);
      // onAuthStateChanged(null) fires here, but reauthingRef keeps overlay up
      await signInWithPopup(auth, provider);
      // onAuthStateChanged(user) fires → clears overlay via reauthingRef
    } catch {
      // Popup was blocked or user closed it — reset state gracefully
      reauthingRef.current = false;
      setIsReauthing(false);
      // Restore the signed-out state so app shows login page
      setUser(null);
      setIsAuthenticated(false);
      setUserName(''); setUserEmail(''); setFamilyRole('parent');
      window.dispatchEvent(new CustomEvent('tokenExpired'));
    }
  };

  const logout = async () => {
    try { await signOut(auth); } catch (_) {}
    setUser(null); setIsAuthenticated(false);
    setNeedOnboarding(false); setNeedPwdChanged(false);
    setUserName(''); setUserEmail(''); setFamilyRole('parent'); setAvatarUrl('');
    setOnboardingRole('parent'); setTshirtSize('');
  };

  const setOnboardingComplete = useCallback(() => {
    setNeedOnboarding(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, userName, userEmail, familyRole, avatarUrl,
      isAuthenticated, isLoading, needOnboarding, needPwdChanged,
      isReauthing, isSessionExpired,
      onboardingRole, tshirtSize,
      login, loginWithGoogle, logout, reauthWithRedirect,
      setOnboardingComplete, fetchUserProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
